import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useEffect } from "react";
import { ReactElement } from "react";
import { useCallback } from "react";
import { useState } from "react";

import TooltipButton from "~frontend/components/TooltipButton";
import OptedOutNumberDetail from "~frontend/components/optouts/OptedOutNumberDetail";
import { useOptedOutNumberDetails } from "~frontend/components/optouts/useOptedOutNumberDetails";
import theme from "~frontend/theme";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";
import usePrevious from "~frontend/util/usePrevious";

export default function OptInDialog({
  phoneNumber,
  deleteOptOut,
  open,
  onClose,
}: {
  phoneNumber: string;
  deleteOptOut: () => Promise<any>;
  open: boolean;
  onClose: () => void;
}): ReactElement {
  const confirm = useConfirm();

  const { data, loading, error, refetch, canDelete, cannotDeleteReason } =
    useOptedOutNumberDetails(phoneNumber);

  const previouslyOpen = usePrevious(open);
  useEffect(() => {
    if (!previouslyOpen && open) {
      refetch();
    }
  }, [previouslyOpen, open, refetch]);

  const [deleting, setDeleting] = useState(false);

  const handleDeleteOptOut = useCallback(async () => {
    if (deleting || !canDelete) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Reverse Opt Out",
      description: `Are you sure you want to opt ${prettyPhoneNumber(
        phoneNumber
      )} back in?`,
    });
    if (!confirmed) {
      return;
    }

    setDeleting(true);
    await deleteOptOut();
    setDeleting(false);
  }, [deleting, canDelete, confirm, phoneNumber, deleteOptOut]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogContent>
        <OptedOutNumberDetail
          phoneNumber={phoneNumber}
          optOutDetails={data}
          optOutDetailsLoading={loading}
          optOutDetailsError={error}
        />
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "space-between",
          paddingRight: theme.spacing(3),
          paddingLeft: theme.spacing(3),
          paddingBottom: theme.spacing(3),
        }}
      >
        {data && !data.isTextable ? (
          <TooltipButton
            disabled={deleting || !canDelete}
            variant="outlined"
            color="error"
            tooltipText={!canDelete ? cannotDeleteReason : null}
            onClick={handleDeleteOptOut}
          >
            {deleting ? "Reversing..." : "Reverse Opt Out"}
          </TooltipButton>
        ) : (
          <Box />
        )}
        <Button
          disabled={deleting}
          variant="contained"
          color="primary"
          onClick={onClose}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
